/**
 * Billing API
 * Handles billing, subscription, and token usage endpoints
 */

import { post, get } from "./baseClient";
import type { BillingInfo } from "../types/billing";

// --- Types ---

export interface MonthlyTokenUsageStats {
  month: string; // ISO date string from Go time.Time
  totalTokens: number;
}

export interface TokenBudgetStatus {
  canProceed: boolean;
  remainingTokens: number;
  monthlyLimit: number;
}

export interface InvoiceInfo {
  id: string;
  number: string | null;
  amount_paid: number; // In cents
  status: string; // paid, open, void, uncollectible, etc.
  currency: string;
  created: string; // ISO date string
  invoice_pdf?: string; // URL to download PDF
  hosted_invoice_url?: string; // URL to view invoice
}

// --- API Functions ---

/**
 * Creates a Stripe checkout session for subscription
 */
export async function createCheckoutSession(
  planIdentifier: string
): Promise<string> {
  const response = await post<{ checkoutUrl: string }>(
    "/billing/checkout-session",
    { plan_identifier: planIdentifier },
    true
  );
  return response.checkoutUrl;
}

/**
 * Creates a Stripe customer portal session
 */
export async function createCustomerPortalSession(): Promise<string> {
  const response = await post<{ url: string }>(
    "/billing/customer-portal",
    {},
    true
  );
  return response.url;
}

/**
 * Gets billing status for the authenticated user
 * @lintignore - Public API function, kept for backward compatibility
 */
export async function getBillingStatus(): Promise<BillingInfo | null> {
  return get<BillingInfo | null>("/billing/status", true);
}

/**
 * Gets verified billing status (checks with Stripe)
 */
export async function getVerifiedBillingStatus(): Promise<BillingInfo | null> {
  return get<BillingInfo | null>("/billing/verified-status", true);
}

/**
 * Gets monthly token usage statistics
 */
export async function getMonthlyTokenUsage(): Promise<
  MonthlyTokenUsageStats[]
> {
  return get<MonthlyTokenUsageStats[]>("/billing/token-usage/monthly", true);
}

/**
 * Gets current token budget status
 */
export async function getTokenBudgetStatus(): Promise<TokenBudgetStatus> {
  return get<TokenBudgetStatus>("/billing/token-budget", true);
}

/**
 * Gets invoice history for the authenticated user
 */
export async function getInvoiceHistory(
  limit?: number
): Promise<InvoiceInfo[]> {
  const params = limit ? `?limit=${limit}` : "";
  const response = await get<{ invoices: InvoiceInfo[] }>(
    `/billing/invoices${params}`,
    true
  );
  return response.invoices;
}

/**
 * Creates a Setup Intent for subscription checkout
 */
export async function createSetupIntent(
  planIdentifier: string
): Promise<string> {
  const response = await post<{ clientSecret: string }>(
    "/billing/setup-intent",
    { plan_identifier: planIdentifier },
    true
  );
  return response.clientSecret;
}

/**
 * Confirms a subscription after setup intent succeeds
 */
export async function confirmSubscription(
  setupIntentId: string
): Promise<void> {
  await post<void>(
    "/billing/confirm-subscription",
    { setup_intent_id: setupIntentId },
    true
  );
}

/**
 * Cancels a subscription (either immediately or at period end)
 */
export async function cancelSubscription(
  cancelAtPeriodEnd: boolean
): Promise<void> {
  await post<void>(
    "/billing/cancel-subscription",
    { cancel_at_period_end: cancelAtPeriodEnd },
    true
  );
}

/**
 * Changes a subscription to a different plan
 */
export async function changeSubscriptionPlan(
  newPlanIdentifier: string
): Promise<void> {
  await post<void>(
    "/billing/change-plan",
    { new_plan_identifier: newPlanIdentifier },
    true
  );
}
